import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Building2, Users as UsersIcon, Briefcase, Package, Trash2, Plus, X, Pencil, ExternalLink } from "lucide-react";
import AdminSupportConsole from "@/components/AdminSupportConsole";
import TwilioNumbersMarketplace from "@/components/TwilioNumbersMarketplace";
import AdminQuestionnairesPanel from "@/components/AdminQuestionnairesPanel";

const PLAN_LIMITS = { free: 1, starter: 5, professional: 25, enterprise: 100 };

export default function SuperAdmin() {
  const { t, lang } = useI18n();
  const [stats, setStats] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [applications, setApplications] = useState([]);
  const [clientRequests, setClientRequests] = useState([]);
  const [tab, setTab] = useState("companies"); // companies | users | applications | requests | support | numbers
  const [showCreate, setShowCreate] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [appDetail, setAppDetail] = useState(null);
  const [reqDetail, setReqDetail] = useState(null);
  const [supportUnread, setSupportUnread] = useState(0);

  const load = async () => {
    try {
      const [s, c, u, a, r] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/companies"),
        api.get("/admin/users"),
        api.get("/admin/partner-applications"),
        api.get("/admin/client-requests"),
      ]);
      setStats(s.data); setCompanies(c.data); setUsers(u.data); setApplications(a.data); setClientRequests(r.data);
    } catch { /* handled by interceptor */ }
  };
  useEffect(() => { load(); }, []);

  // Poll support-unread badge every 15s
  useEffect(() => {
    const loadUnread = async () => {
      try {
        const { data } = await api.get("/admin/support/unread-total");
        setSupportUnread(data.unread || 0);
      } catch { /* ignore */ }
    };
    loadUnread();
    const id = setInterval(loadUnread, 15000);
    return () => clearInterval(id);
  }, []);

  const updatePlan = async (id, plan) => {
    await api.patch(`/admin/companies/${id}`, { subscription_plan: plan, max_users: PLAN_LIMITS[plan] });
    load();
  };
  const delCompany = async (id) => {
    if (!window.confirm(lang === "el" ? "Διαγραφή εταιρείας και ΟΛΩΝ των δεδομένων;" : "Delete company and ALL data?")) return;
    await api.delete(`/admin/companies/${id}`); load();
  };
  const delUser = async (id, name) => {
    if (!window.confirm(`${lang === "el" ? "Διαγραφή χρήστη" : "Delete user"}: ${name}?`)) return;
    await api.delete(`/admin/users/${id}`); load();
  };

  if (!stats) return <div>{t("common.loading")}</div>;

  const KpiCard = ({ icon: Icon, label, value, accent = "#0052CC", testid }) => (
    <div data-testid={testid} className="bg-white border border-slate-200 rounded-lg p-3 sm:p-5">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[10px] sm:text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 truncate">{label}</div>
          <div className="mt-2 font-heading text-2xl sm:text-3xl font-bold text-slate-900">{value}</div>
        </div>
        <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-md flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${accent}15`, color: accent }}>
          <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
        </div>
      </div>
    </div>
  );

  const L = lang === "el";

  return (
    <div className="space-y-8">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">Super Admin</div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight mt-1">
          {L ? "Επισκόπηση Πλατφόρμας" : "Platform Overview"}
        </h1>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
        <KpiCard icon={Building2} label={L ? "Εταιρείες" : "Companies"} value={stats.total_companies} testid="admin-kpi-companies" />
        <KpiCard icon={UsersIcon} label={L ? "Χρήστες" : "Users"} value={stats.total_users} accent="#8B5CF6" testid="admin-kpi-users" />
        <KpiCard icon={Briefcase} label={L ? "Ευκαιρίες" : "Opportunities"} value={stats.total_opportunities} accent="#06B6D4" testid="admin-kpi-opps" />
        <KpiCard icon={Package} label={L ? "Προϊόντα" : "Products"} value={stats.total_products} accent="#F97316" testid="admin-kpi-products" />
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 border-b border-slate-200 overflow-x-auto whitespace-nowrap -mx-4 px-4 sm:mx-0 sm:px-0">        <TabBtn active={tab === "companies"} onClick={() => setTab("companies")} testid="tab-companies">
          {L ? "Εταιρείες" : "Companies"} <span className="ml-1.5 text-xs opacity-70">({companies.length})</span>
        </TabBtn>
        <TabBtn active={tab === "users"} onClick={() => setTab("users")} testid="tab-users">
          {L ? "Χρήστες" : "Users"} <span className="ml-1.5 text-xs opacity-70">({users.length})</span>
        </TabBtn>
        <TabBtn active={tab === "applications"} onClick={() => setTab("applications")} testid="tab-applications">
          {L ? "Αιτήσεις συνεργατών" : "Partner apps"}{" "}
          <span className="ml-1.5 text-xs opacity-70">({applications.length})</span>
          {applications.filter((a) => a.status === "new").length > 0 && (
            <span className="ml-1.5 inline-block w-2 h-2 rounded-full bg-amber-500" />
          )}
        </TabBtn>
        <TabBtn active={tab === "requests"} onClick={() => setTab("requests")} testid="tab-requests">
          {L ? "Αιτήματα πελατών" : "Client requests"}{" "}
          <span className="ml-1.5 text-xs opacity-70">({clientRequests.length})</span>
          {clientRequests.filter((r) => r.status === "new").length > 0 && (
            <span className="ml-1.5 inline-block w-2 h-2 rounded-full bg-emerald-500" />
          )}
        </TabBtn>
        <TabBtn active={tab === "support"} onClick={() => setTab("support")} testid="tab-support">
          {L ? "Υποστήριξη" : "Support"}
          {supportUnread > 0 && (
            <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold">{supportUnread}</span>
          )}
        </TabBtn>
        <TabBtn active={tab === "numbers"} onClick={() => setTab("numbers")} testid="tab-numbers">
          {L ? "Νούμερα Twilio" : "Twilio numbers"}
        </TabBtn>
        <TabBtn active={tab === "questionnaires"} onClick={() => setTab("questionnaires")} testid="tab-questionnaires">
          {L ? "Ερωτηματολόγια" : "Questionnaires"}
        </TabBtn>
      </div>

      {tab === "companies" && (
        <div className="bg-white border border-slate-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <h2 className="font-heading text-xl font-semibold text-slate-900">{L ? "Εταιρείες" : "Companies"}</h2>
            <button data-testid="admin-create-company-button" onClick={() => setShowCreate(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-[#0052CC] text-white rounded-md text-sm font-semibold hover:bg-[#0043A8]">
              <Plus className="w-4 h-4" /> {L ? "Νέα εταιρεία" : "New company"}
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-companies-table">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  {[L ? "Όνομα" : "Name", L ? "Ιδιοκτήτης" : "Owner", L ? "Χώρα" : "Country", L ? "Πλάνο" : "Plan", L ? "Χρήστες" : "Users", "Opps", L ? "Twilio #" : "Twilio #", L ? "Ενέργειες" : "Actions"].map((h) => (
                    <th key={h} className="py-3 px-4 text-left text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {companies.length === 0 ? (
                  <tr><td colSpan={8} className="py-8 text-center text-slate-400">{t("common.no_data")}</td></tr>
                ) : companies.map((c) => (
                  <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-3 px-4 font-medium text-slate-900">
                      <div className="flex items-center gap-2">
                        <span>{c.name}</span>
                        <a href={`/c/${c.slug}`} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-[#0052CC]"><ExternalLink className="w-3.5 h-3.5" /></a>
                      </div>
                      <div className="text-xs text-slate-500">/c/{c.slug}</div>
                    </td>
                    <td className="py-3 px-4">
                      {c.owner ? (
                        <div className="flex items-center gap-2">
                          <div>
                            <div className="text-sm text-slate-900 font-medium">{c.owner.name}</div>
                            <div className="text-xs text-slate-500">{c.owner.email}</div>
                          </div>
                          <button onClick={() => setEditUser(c.owner)} className="p-1 text-slate-400 hover:text-[#0052CC]" data-testid={`edit-owner-${c.id}`} title={L ? "Επεξεργασία" : "Edit"}>
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ) : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="py-3 px-4">{c.country}</td>
                    <td className="py-3 px-4">
                      <select value={c.subscription_plan} onChange={(e) => updatePlan(c.id, e.target.value)} className="text-xs border border-slate-200 rounded px-2 py-1" data-testid={`admin-plan-${c.id}`}>
                        <option value="free">free</option>
                        <option value="starter">starter</option>
                        <option value="professional">professional</option>
                        <option value="enterprise">enterprise</option>
                      </select>
                    </td>
                    <td className="py-3 px-4">{c.user_count}/{c.max_users}</td>
                    <td className="py-3 px-4">{c.opp_count}</td>
                    <td className="py-3 px-4">
                      <TwilioNumberInput company={c} onSaved={load} L={L} />
                    </td>
                    <td className="py-3 px-4 text-right">
                      <button onClick={() => delCompany(c.id)} className="p-1.5 text-slate-500 hover:text-red-600" data-testid={`delete-company-${c.id}`}><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "users" && (
        <div className="bg-white border border-slate-200 rounded-lg p-6">
          <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">{L ? "Όλοι οι χρήστες" : "All users"}</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-users-table">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  {[L ? "Όνομα" : "Name", "Email", L ? "Ρόλος" : "Role", L ? "Εταιρεία" : "Company", L ? "Εργατώρες" : "Hours", L ? "Κατάσταση" : "Status", L ? "Ενέργειες" : "Actions"].map((h) => (
                    <th key={h} className="py-3 px-4 text-left text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-3 px-4 font-medium text-slate-900">{u.name}</td>
                    <td className="py-3 px-4 text-slate-700">{u.email}</td>
                    <td className="py-3 px-4">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded ${u.role === "super_admin" ? "bg-amber-100 text-amber-800" : u.role === "company_owner" ? "bg-[#0052CC]/10 text-[#0052CC]" : "bg-slate-100 text-slate-700"}`}>
                        {u.role.replace("_", " ")}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-slate-600">{u.company_name || "—"}</td>
                    <td className="py-3 px-4 text-slate-700 text-xs whitespace-nowrap">
                      {u.role === "sales_rep" ? (
                        <div data-testid={`hours-${u.id}`} className="space-y-0.5">
                          <div><strong>{u.allocated_hours ?? 0}h</strong> <span className="text-slate-400">{L ? "εργατώρες" : "work"}</span></div>
                          <div><strong>{u.talk_minutes_allocated ?? 0}min</strong> <span className="text-slate-400">{L ? "ομιλίας" : "talk"}</span></div>
                        </div>
                      ) : u.role === "company_owner" ? (
                        <div data-testid={`hours-${u.id}`}>
                          <strong>{u.talk_minutes_allocated ?? 0}min</strong> <span className="text-slate-400">{L ? "ομιλίας" : "talk"}</span>
                        </div>
                      ) : "—"}
                    </td>
                    <td className="py-3 px-4">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded ${u.is_active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                        {u.is_active ? (L ? "Ενεργός" : "Active") : (L ? "Ανενεργός" : "Inactive")}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <button onClick={() => setEditUser(u)} className="p-1.5 text-slate-500 hover:text-[#0052CC]" data-testid={`edit-user-${u.id}`}><Pencil className="w-4 h-4" /></button>
                      {u.role !== "super_admin" && (
                        <button onClick={() => delUser(u.id, u.name)} className="p-1.5 text-slate-500 hover:text-red-600" data-testid={`delete-user-${u.id}`}><Trash2 className="w-4 h-4" /></button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showCreate && <CreateCompanyModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} L={L} />}
      {editUser && <EditUserModal user={editUser} onClose={() => setEditUser(null)} onSaved={() => { setEditUser(null); load(); }} L={L} />}
      {appDetail && <ApplicationDetailModal app={appDetail} onClose={() => setAppDetail(null)} onSaved={() => { setAppDetail(null); load(); }} L={L} />}
      {reqDetail && <ClientRequestModal req={reqDetail} onClose={() => setReqDetail(null)} onSaved={() => { setReqDetail(null); load(); }} L={L} />}

      {tab === "requests" && (
        <div className="bg-white border border-slate-200 rounded-lg p-6">
          <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
            {L ? "Αιτήματα πελατών για συνεργάτη" : "Client requests for partners"}
          </h2>
          {clientRequests.length === 0 ? (
            <div className="py-12 text-center text-slate-400">{L ? "Κανένα αίτημα ακόμη." : "No requests yet."}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="requests-table">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50">
                    {[L ? "Ημ/νία" : "Date", L ? "Εταιρεία" : "Company", L ? "Υπεύθυνος" : "Contact", L ? "Χώρες" : "Countries", L ? "Στόχοι" : "Goals", L ? "Κατάσταση" : "Status"].map((h) => (
                      <th key={h} className="py-3 px-4 text-left text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {clientRequests.map((r) => (
                    <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setReqDetail(r)} data-testid={`req-row-${r.id}`}>
                      <td className="py-3 px-4 text-slate-700 whitespace-nowrap text-xs">
                        {new Date(r.created_at).toLocaleDateString(L ? "el-GR" : "en-GB")}
                      </td>
                      <td className="py-3 px-4">
                        <div className="font-medium text-slate-900">{r.company_name}</div>
                        <div className="text-xs text-slate-500">{r.email}</div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="text-slate-900">{r.contact_person}</div>
                        <div className="text-xs text-slate-500">{r.phone || "—"}</div>
                      </td>
                      <td className="py-3 px-4 text-xs">{(r.target_countries || []).slice(0, 3).join(", ")}{r.target_countries?.length > 3 ? "..." : ""}</td>
                      <td className="py-3 px-4 text-xs">{(r.goals || []).length}</td>
                      <td className="py-3 px-4">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded ${
                          r.status === "new" ? "bg-emerald-100 text-emerald-800" :
                          r.status === "matched" ? "bg-blue-100 text-blue-800" :
                          r.status === "closed" ? "bg-slate-100 text-slate-700" :
                          "bg-amber-100 text-amber-800"
                        }`}>{r.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === "applications" && (
        <div className="bg-white border border-slate-200 rounded-lg p-6">
          <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
            {L ? "Αιτήσεις συνεργατών" : "Partner applications"}
          </h2>
          {applications.length === 0 ? (
            <div className="py-12 text-center text-slate-400">{L ? "Καμία αίτηση ακόμη." : "No applications yet."}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="applications-table">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50">
                    {[L ? "Ημ/νία" : "Date", L ? "Όνομα" : "Name", L ? "Χώρα" : "Country", L ? "Εμπειρία" : "Experience", L ? "Τομείς" : "Sectors", L ? "Κατάσταση" : "Status", ""].map((h) => (
                      <th key={h} className="py-3 px-4 text-left text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {applications.map((a) => (
                    <tr key={a.id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setAppDetail(a)} data-testid={`app-row-${a.id}`}>
                      <td className="py-3 px-4 text-slate-700 whitespace-nowrap text-xs">
                        {new Date(a.created_at).toLocaleDateString(L ? "el-GR" : "en-GB")}
                      </td>
                      <td className="py-3 px-4">
                        <div className="font-medium text-slate-900">{a.full_name}</div>
                        <div className="text-xs text-slate-500">{a.email}</div>
                      </td>
                      <td className="py-3 px-4">{a.country}{a.city ? `, ${a.city}` : ""}</td>
                      <td className="py-3 px-4">{a.sales_experience_years}y</td>
                      <td className="py-3 px-4 text-xs text-slate-600 max-w-xs truncate">{a.preferred_sectors?.length || 0} sectors</td>
                      <td className="py-3 px-4">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded ${
                          a.status === "new" ? "bg-amber-100 text-amber-800" :
                          a.status === "accepted" ? "bg-emerald-100 text-emerald-800" :
                          a.status === "rejected" ? "bg-red-100 text-red-700" :
                          a.status === "interview" ? "bg-blue-100 text-blue-800" :
                          "bg-slate-100 text-slate-700"
                        }`}>{a.status}</span>
                      </td>
                      <td className="py-3 px-4 text-slate-400">→</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === "support" && (
        <AdminSupportConsole L={L} />
      )}

      {tab === "numbers" && (
        <TwilioNumbersMarketplace L={L} companies={companies} />
      )}

      {tab === "questionnaires" && (
        <AdminQuestionnairesPanel L={L} />
      )}
    </div>
  );
}

function ClientRequestModal({ req, onClose, onSaved, L }) {
  const [status, setStatus] = useState(req.status);
  const [notes, setNotes] = useState(req.admin_notes || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/client-requests/${req.id}`, { status, admin_notes: notes });
      onSaved();
    } finally { setSaving(false); }
  };
  const del = async () => {
    if (!window.confirm(L ? "Διαγραφή αιτήματος;" : "Delete?")) return;
    await api.delete(`/admin/client-requests/${req.id}`);
    onSaved();
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col my-8" onClick={(e) => e.stopPropagation()} data-testid="req-detail-modal">
        <div className="px-6 py-4 border-b border-slate-200 flex items-start justify-between bg-gradient-to-br from-[#0F172A] to-[#1E3A8A] text-white">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-white/70">Client Request</div>
            <h3 className="font-heading text-2xl font-bold tracking-tight">{req.company_name}</h3>
            <div className="text-sm text-white/80">{req.contact_person}{req.position ? ` · ${req.position}` : ""}{req.email ? ` · ${req.email}` : ""}</div>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white">✕</button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5 text-sm">
          <div className="grid grid-cols-2 gap-4">
            <Section label={L ? "Κλάδος" : "Industry"} value={req.industry || "—"} />
            <Section label={L ? "Ιστοσελίδα" : "Website"} value={req.website || "—"} />
            <Section label={L ? "Εργαζόμενοι" : "Employees"} value={req.employees || "—"} />
            <Section label={L ? "Κύκλος εργασιών" : "Revenue"} value={req.annual_revenue || "—"} />
          </div>
          <Section label={L ? "Υφιστάμενες εξαγωγές" : "Current exports"} multiline={req.current_export_markets || "—"} />
          <Section label={L ? "Χώρες στόχοι" : "Target countries"} value={(req.target_countries || []).join(", ") || "—"} />
          <Section label={L ? "Τύπος πελατών" : "Customer types"} value={(req.customer_types || []).join(", ") || "—"} />
          <div className="pt-3 border-t border-slate-200">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-400 mb-2">{L ? "Προφίλ εκπροσώπου" : "Rep profile"}</div>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div><span className="text-slate-500">Education:</span> <strong>{req.rep_education || "—"}</strong></div>
              <div><span className="text-slate-500">Residence:</span> <strong>{req.rep_residence || "—"}</strong></div>
              <div><span className="text-slate-500">Sales exp:</span> <strong>{req.rep_sales_experience || "—"}</strong></div>
              <div><span className="text-slate-500">Industry exp:</span> <strong>{req.rep_industry_experience || "—"}</strong></div>
              <div className="col-span-2"><span className="text-slate-500">Languages:</span> <strong>{(req.rep_languages || []).join(", ") || "—"}</strong></div>
              <div className="col-span-2"><span className="text-slate-500">Network:</span> <strong>{req.rep_existing_network || "—"}</strong></div>
            </div>
          </div>
          <Section label={L ? "Αμοιβή" : "Compensation"} value={`€${req.hourly_rate_min || 0} - €${req.hourly_rate_max || 0}/h · ${req.hours_per_week || 0}h/εβδ · Bonus: ${req.performance_bonus || "—"}`} />
          <Section label={L ? "Στόχοι" : "Goals"} value={(req.goals || []).join(", ")} multiline={req.goals_description} />
          {req.product_description && <Section label={L ? "Προϊόντα" : "Products"} multiline={req.product_description} />}
          {req.competitive_advantages && <Section label={L ? "Πλεονεκτήματα" : "Advantages"} multiline={req.competitive_advantages} />}
          {(req.uploaded_file_ids || []).length > 0 && (
            <Section label={L ? "Αρχεία" : "Files"} value={`${req.uploaded_file_ids.length} ${L ? "αρχεία" : "files"}`} />
          )}

          <div className="pt-4 border-t border-slate-200">
            <label className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-400">{L ? "Κατάσταση" : "Status"}</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-md text-sm" data-testid="req-status">
              <option value="new">new</option>
              <option value="in_review">in_review</option>
              <option value="matched">matched</option>
              <option value="closed">closed</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-400">{L ? "Σημειώσεις admin" : "Admin notes"}</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-md text-sm" data-testid="req-notes" />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between bg-slate-50">
          <button onClick={del} className="text-sm text-red-600 hover:underline">{L ? "Διαγραφή" : "Delete"}</button>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 border border-slate-200 rounded-md text-sm font-medium bg-white">{L ? "Κλείσιμο" : "Close"}</button>
            <button onClick={save} disabled={saving} data-testid="req-save" className="px-4 py-2 bg-[#0052CC] text-white rounded-md text-sm font-semibold disabled:opacity-50">{saving ? "..." : (L ? "Αποθήκευση" : "Save")}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ApplicationDetailModal({ app, onClose, onSaved, L }) {
  const [status, setStatus] = useState(app.status);
  const [notes, setNotes] = useState(app.admin_notes || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/partner-applications/${app.id}`, { status, admin_notes: notes });
      onSaved();
    } finally { setSaving(false); }
  };

  const del = async () => {
    if (!window.confirm(L ? "Διαγραφή αίτησης;" : "Delete application?")) return;
    await api.delete(`/admin/partner-applications/${app.id}`);
    onSaved();
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col my-8" onClick={(e) => e.stopPropagation()} data-testid="app-detail-modal">
        <div className="px-6 py-4 border-b border-slate-200 flex items-start justify-between">
          <div>
            <h3 className="font-heading text-xl font-bold text-slate-900">{app.full_name}</h3>
            <div className="text-sm text-slate-500">{app.email}{app.phone ? ` · ${app.phone}` : ""}</div>
          </div>
          <button onClick={onClose}><Pencil className="w-4 h-4 rotate-45" /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4 text-sm">
          <Section label={L ? "Τόπος κατοικίας" : "Residence"} value={`${app.city || ""}${app.city ? ", " : ""}${app.country}${app.permanent_residence ? ` · ${app.permanent_residence}` : ""}`} />
          <Section label={L ? "Εκπαίδευση" : "Education"} value={`${app.education_level || "—"}${app.field_of_study ? ` · ${app.field_of_study}` : ""}`} />
          <Section label={L ? "Εμπειρία πωλήσεων" : "Sales experience"} value={`${app.sales_experience_years} ${L ? "χρόνια" : "years"}`} multiline={app.sales_experience_description} />
          <Section label={L ? "Υπάρχον δίκτυο" : "Existing network"} multiline={app.existing_network || "—"} />
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-400 mb-1">{L ? "Τομείς" : "Sectors"}</div>
            <div className="flex flex-wrap gap-1.5">
              {app.preferred_sectors?.map((s) => <span key={s} className="text-xs px-2 py-0.5 bg-[#0052CC]/10 text-[#0052CC] rounded font-semibold">{s}</span>)}
            </div>
          </div>
          <Section label={L ? "Γλώσσες" : "Languages"} value={app.languages?.join(", ") || "—"} />
          <Section label={L ? "Διαθέσιμος για ραντεβού" : "Available for meetings"} value={app.available_for_meetings ? (L ? "Ναι" : "Yes") : (L ? "Όχι" : "No")} />
          {app.linkedin && <Section label="LinkedIn" value={<a href={app.linkedin} target="_blank" rel="noreferrer" className="text-[#0052CC] hover:underline">{app.linkedin}</a>} />}
          {app.expected_compensation && <Section label={L ? "Επιθυμητή αμοιβή" : "Expected compensation"} value={app.expected_compensation} />}
          {app.why_join && <Section label={L ? "Κίνητρο" : "Motivation"} multiline={app.why_join} />}

          <div className="pt-4 border-t border-slate-200">
            <label className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-400">{L ? "Κατάσταση" : "Status"}</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-md text-sm" data-testid="app-status-select">
              <option value="new">new</option>
              <option value="in_review">in_review</option>
              <option value="interview">interview</option>
              <option value="accepted">accepted</option>
              <option value="rejected">rejected</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-400">{L ? "Σημειώσεις admin" : "Admin notes"}</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-md text-sm" data-testid="app-admin-notes" />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between bg-slate-50">
          <button onClick={del} className="text-sm text-red-600 hover:underline" data-testid="app-delete">{L ? "Διαγραφή" : "Delete"}</button>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 border border-slate-200 rounded-md text-sm font-medium bg-white">{L ? "Κλείσιμο" : "Close"}</button>
            <button onClick={save} disabled={saving} data-testid="app-save" className="px-4 py-2 bg-[#0052CC] text-white rounded-md text-sm font-semibold disabled:opacity-50">{saving ? "..." : (L ? "Αποθήκευση" : "Save")}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ label, value, multiline }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-400 mb-1">{label}</div>
      {multiline ? (
        <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{multiline}</p>
      ) : (
        <div className="text-sm text-slate-700">{value}</div>
      )}
    </div>
  );
}

function TabBtn({ children, active, onClick, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${active ? "border-[#0052CC] text-[#0052CC]" : "border-transparent text-slate-600 hover:text-slate-900"}`}
    >
      {children}
    </button>
  );
}

function CreateCompanyModal({ onClose, onCreated, L }) {
  const [form, setForm] = useState({
    company_name: "", industry: "", country: "Greece", subscription_plan: "starter",
    owner_name: "", owner_email: "", owner_password: "", owner_language: "el",
  });
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setSaving(true);
    try {
      await api.post("/admin/companies", form);
      onCreated();
    } catch (e) {
      setErr(e.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
          <h3 className="font-heading font-semibold text-slate-900">{L ? "Νέα εταιρεία πελάτη" : "New customer company"}</h3>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="px-6 py-4 space-y-3" data-testid="create-company-form">
          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-400">{L ? "Εταιρεία" : "Company"}</div>
          <Inp label={L ? "Όνομα εταιρείας" : "Company name"} testid="cc-name" required value={form.company_name} onChange={(v) => setForm({ ...form, company_name: v })} />
          <Inp label={L ? "Κλάδος" : "Industry"} testid="cc-industry" value={form.industry} onChange={(v) => setForm({ ...form, industry: v })} />
          <Inp label={L ? "Χώρα" : "Country"} testid="cc-country" value={form.country} onChange={(v) => setForm({ ...form, country: v })} />
          <div>
            <label className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-700">{L ? "Πλάνο" : "Plan"}</label>
            <select data-testid="cc-plan" value={form.subscription_plan} onChange={(e) => setForm({ ...form, subscription_plan: e.target.value })} className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-md text-sm">
              <option value="free">free (1 χρήστης)</option>
              <option value="starter">starter (5 χρήστες)</option>
              <option value="professional">professional (25 χρήστες)</option>
              <option value="enterprise">enterprise (100 χρήστες)</option>
            </select>
          </div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-400 pt-2 border-t border-slate-100">{L ? "Ιδιοκτήτης" : "Owner"}</div>
          <Inp label={L ? "Όνομα ιδιοκτήτη" : "Owner name"} testid="cc-owner-name" required value={form.owner_name} onChange={(v) => setForm({ ...form, owner_name: v })} />
          <Inp label="Email" testid="cc-owner-email" type="email" required value={form.owner_email} onChange={(v) => setForm({ ...form, owner_email: v })} />
          <Inp label={L ? "Κωδικός" : "Password"} testid="cc-owner-password" type="password" required value={form.owner_password} onChange={(v) => setForm({ ...form, owner_password: v })} />

          {err && <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-md">{err}</div>}
          <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-slate-200 rounded-md text-sm font-medium">{L ? "Ακύρωση" : "Cancel"}</button>
            <button type="submit" disabled={saving} data-testid="cc-submit" className="px-4 py-2 bg-[#0052CC] text-white rounded-md text-sm font-semibold disabled:opacity-50">
              {saving ? "..." : (L ? "Δημιουργία" : "Create")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditUserModal({ user, onClose, onSaved, L }) {
  const [form, setForm] = useState({
    name: user.name || "",
    email: user.email || "",
    password: "",
    phone: user.phone || "",
    country: user.country || "",
    is_active: user.is_active !== false,
    allocated_hours: user.allocated_hours ?? 0,
    talk_minutes_allocated: user.talk_minutes_allocated ?? 0,
    forward_when_offline: user.forward_when_offline !== false,
  });
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  const isRep = user.role === "sales_rep";
  const isOwner = user.role === "company_owner";
  const canHaveMinutes = isRep || isOwner;

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.password) delete payload.password;
      if (!isRep) {
        delete payload.allocated_hours;
        delete payload.forward_when_offline;
      } else {
        payload.allocated_hours = parseFloat(payload.allocated_hours) || 0;
      }
      if (!canHaveMinutes) {
        delete payload.talk_minutes_allocated;
      } else {
        payload.talk_minutes_allocated = parseInt(payload.talk_minutes_allocated) || 0;
      }
      await api.patch(`/admin/users/${user.id}`, payload);
      onSaved();
    } catch (e) {
      setErr(e.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 className="font-heading font-semibold text-slate-900">{L ? "Επεξεργασία χρήστη" : "Edit user"}</h3>
            <div className="text-xs text-slate-500">{user.role.replace("_", " ")}</div>
          </div>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="px-6 py-4 space-y-3" data-testid="edit-user-form">
          <Inp label={L ? "Όνομα" : "Name"} testid="eu-name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
          <Inp label="Email" testid="eu-email" type="email" required value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
          <div>
            <label className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-700">
              {L ? "Νέος κωδικός (αφήστε κενό για διατήρηση)" : "New password (leave empty to keep)"}
            </label>
            <input data-testid="eu-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-md text-sm" placeholder="••••••••" />
          </div>
          <Inp label={L ? "Τηλέφωνο" : "Phone"} testid="eu-phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
          <Inp label={L ? "Χώρα" : "Country"} testid="eu-country" value={form.country} onChange={(v) => setForm({ ...form, country: v })} />
          {isRep && (
            <>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-700">
                  {L ? "Εργατώρες (συνολικές διαθέσιμες)" : "Allocated work hours"}
                </label>
                <input
                  data-testid="eu-allocated-hours"
                  type="number"
                  step="0.5"
                  min="0"
                  value={form.allocated_hours}
                  onChange={(e) => setForm({ ...form, allocated_hours: e.target.value })}
                  className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-md text-sm"
                />
                <div className="text-[11px] text-slate-500 mt-1">
                  {L
                    ? `Καταγεγραμμένες από Αναφορά Εργασίας: ${user.used_hours ?? 0}h`
                    : `Logged via Work Reports: ${user.used_hours ?? 0}h`}
                </div>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    data-testid="eu-forward-when-offline"
                    type="checkbox"
                    checked={!!form.forward_when_offline}
                    onChange={(e) => setForm({ ...form, forward_when_offline: e.target.checked })}
                  />
                  <span className="text-sm font-semibold text-slate-800">
                    {L ? "Προώθηση εισερχομένων στο κινητό όταν είναι offline" : "Forward incoming to mobile when offline"}
                  </span>
                </label>
                <div className="text-[11px] text-slate-500 mt-1">
                  {L
                    ? `Στέλνει την εισερχόμενη κλήση στο πεδίο "Τηλέφωνο" του πωλητή (${user.phone || "—"}) όταν δεν είναι συνδεδεμένος στην πλατφόρμα.`
                    : `Sends incoming call to the rep's "Phone" field (${user.phone || "—"}) when not logged in.`}
                </div>
              </div>
            </>
          )}

          {canHaveMinutes && (
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-700">
                {L ? "Λεπτά ομιλίας Twilio (διαθέσιμα)" : "Talk minutes (Twilio)"}
              </label>
              <input
                data-testid="eu-talk-minutes"
                type="number"
                step="1"
                min="0"
                value={form.talk_minutes_allocated}
                onChange={(e) => setForm({ ...form, talk_minutes_allocated: e.target.value })}
                className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-md text-sm"
              />
              <div className="text-[11px] text-slate-500 mt-1">
                {L
                  ? `Χρησιμοποιημένα: ${user.talk_minutes_used ?? 0} λεπτά · Υπόλοιπο: ${Math.max((parseInt(form.talk_minutes_allocated) || 0) - (user.talk_minutes_used ?? 0), 0)} λεπτά`
                  : `Used: ${user.talk_minutes_used ?? 0} min · Remaining: ${Math.max((parseInt(form.talk_minutes_allocated) || 0) - (user.talk_minutes_used ?? 0), 0)} min`}
              </div>
            </div>
          )}
          <div className="flex items-center gap-2">
            <input id="eu-active" type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} data-testid="eu-is-active" />
            <label htmlFor="eu-active" className="text-sm text-slate-700">{L ? "Ενεργός" : "Active"}</label>
          </div>

          {err && <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-md">{err}</div>}
          <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-slate-200 rounded-md text-sm font-medium">{L ? "Ακύρωση" : "Cancel"}</button>
            <button type="submit" disabled={saving} data-testid="eu-submit" className="px-4 py-2 bg-[#0052CC] text-white rounded-md text-sm font-semibold disabled:opacity-50">
              {saving ? "..." : (L ? "Αποθήκευση" : "Save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Inp({ label, testid, value, onChange, type = "text", required }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-700">{label}</label>
      <input data-testid={testid} type={type} required={required} value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
    </div>
  );
}


function TwilioNumberInput({ company, onSaved, L }) {
  const [val, setVal] = useState(company.twilio_number || "");
  const [saving, setSaving] = useState(false);
  const [edited, setEdited] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/companies/${company.id}`, { twilio_number: val.trim() || null });
      setEdited(false);
      onSaved && onSaved();
    } finally { setSaving(false); }
  };

  return (
    <div className="flex items-center gap-1">
      <input
        data-testid={`twilio-number-${company.id}`}
        type="tel"
        value={val}
        onChange={(e) => { setVal(e.target.value); setEdited(true); }}
        placeholder="+30…"
        className="w-32 px-2 py-1 border border-slate-200 rounded text-xs font-mono"
      />
      {edited && (
        <button
          data-testid={`twilio-save-${company.id}`}
          onClick={save}
          disabled={saving}
          className="text-xs px-2 py-1 bg-[#0052CC] text-white rounded font-semibold disabled:opacity-50"
        >
          {saving ? "…" : (L ? "OK" : "OK")}
        </button>
      )}
    </div>
  );
}
